import React, { useState } from 'react';
import {
  Box, Paper, Typography, Button, Autocomplete, TextField, Grid, Alert, Divider, Stack,
} from '@mui/material';
import {
  ArrowForwardRounded as ArrowIcon,
  EditOutlined as EditIcon,
  SaveOutlined as SaveIcon,
  SwapHorizRounded as SwapIcon,
} from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import * as actions from '../../../../redux/actions';
import CustomerBankInfo from '../cards/CustomerBankInfo';

// ส่วน "กำหนด/แก้ไขบัญชีของคู่ยืม-คืน" — แยกออกมาจาก LoanPairCard.js เดิม (ตอนนี้ชื่อ
// LoanPairSummaryCard.js) ตามที่ผู้ใช้ขอให้ย้ายไปหน้า submenu ใหม่ "ตั้งค่าคู่ยืม-คืน"
// (LoanPairSetup.js) แยกจากหน้า "ยืม-คืน" หลักที่เหลือแค่ดูข้อมูล (ใช้บ่อยกว่ามาก) — เก็บ
// เฉพาะส่วนเลือก Customer ฝั่งยืม/คืน + ข้อมูลบัญชีธนาคาร ไม่มีการ์ดสรุปยอด/ตาราง
// ประวัติ/dialog กำหนดยอดคงเหลือเลย (ย้ายไปอยู่ LoanPairSummaryCard.js ทั้งหมด)
export function LoanPairSetupCard({
  pair, customerRows, findCustomer, customerName, onSaved, onCancelNew,
}) {
  const dispatch = useDispatch();
  const isNew = !pair;

  const [editing, setEditing] = useState(isNew);
  const [customerId, setCustomerId] = useState(pair?.customer_id || '');
  const [customerIdRepay, setCustomerIdRepay] = useState(pair?.customer_id_repay || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const canSave = customerId && customerIdRepay;

  const startEdit = () => {
    setCustomerId(pair?.customer_id || '');
    setCustomerIdRepay(pair?.customer_id_repay || '');
    setError('');
    setEditing(true);
  };

  const cancelEdit = () => {
    if (isNew) {
      onCancelNew();
      return;
    }
    setEditing(false);
  };

  const submit = async () => {
    const borrowCustomer = findCustomer(customerId);
    const repayCustomer = findCustomer(customerIdRepay);
    const payload = {
      customerId,
      customerIdRepay,
      bank: borrowCustomer?.bank?.bank_name || '',
      accountNumber: borrowCustomer?.bank?.account_number || '',
      bankRepay: repayCustomer?.bank?.bank_name || '',
      accountNumberRepay: repayCustomer?.bank?.account_number || '',
    };

    setSaving(true);
    setError('');
    try {
      if (isNew) {
        await dispatch(actions.createBankAccount({
          ...payload,
          name: `ยืม-คืน: ${customerName(customerId)} / ${customerName(customerIdRepay)}`,
          mode: 'BALANCE',
        }));
      } else {
        await dispatch(actions.updateBankAccount({ id: pair.id, ...payload }));
        // การ์ด "แก้ไข" อันเดิมยังอยู่ต่อ (pair prop คงที่จนกว่า parent จะ reload)
        // สลับกลับ read-only ได้ทันที เพราะ pair ไม่ใช่ null
        setEditing(false);
      }
      // การ์ด "สร้างใหม่" (isNew) ต้องไม่ setEditing(false) เอง — pair prop ยัง
      // เป็น null อยู่จนกว่า parent จะ reload แล้วเมานต์การ์ดจริงแทน ถ้าสลับเป็น
      // read-only ตอนนี้จะพังเพราะ pair.customer_id อ่านจาก null ไม่ได้
      // (ปล่อยให้ onSaved() ของ parent เป็นคน unmount การ์งนี้แทน)
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 2, md: 2.5 },
        borderRadius: 3,
        borderColor: isNew ? '#93c5fd' : '#dbeafe',
        bgcolor: isNew ? '#f8fbff' : '#fff',
        boxShadow: '0 4px 18px rgba(15,23,42,.04)',
      }}
    >
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {editing ? (
        <Box>
          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 2.5 }}>
            <Box sx={{ width: 42, height: 42, borderRadius: 2, display: 'grid', placeItems: 'center', bgcolor: '#dbeafe', color: '#2563eb' }}><SwapIcon /></Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 750 }}>{isNew ? 'สร้างคู่ยืม-คืนใหม่' : 'แก้ไขคู่ยืม-คืน'}</Typography>
              <Typography variant="body2" color="text.secondary">กำหนดว่า Customer ใดเป็นผู้ยืม และ Customer ใดเป็นผู้รับคืน</Typography>
            </Box>
          </Stack>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={customerRows}
                getOptionLabel={(c) => c.name || ''}
                isOptionEqualToValue={(a, b) => a._id === b._id}
                value={findCustomer(customerId) || null}
                onChange={(e, newValue) => setCustomerId(newValue?._id || '')}
                renderInput={(params) => <TextField {...params} label="1. เลือก Customer ฝั่งยืม" helperText="บัญชีต้นทางของรายการยืม" />}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={customerRows}
                getOptionLabel={(c) => c.name || ''}
                isOptionEqualToValue={(a, b) => a._id === b._id}
                value={findCustomer(customerIdRepay) || null}
                onChange={(e, newValue) => setCustomerIdRepay(newValue?._id || '')}
                renderInput={(params) => <TextField {...params} label="2. เลือก Customer ฝั่งคืน" helperText="บัญชีปลายทางของรายการคืน" />}
              />
            </Grid>
          </Grid>

          <CustomerBankInfo customer={findCustomer(customerId)} labelPrefix="ฝั่งยืม — " sx={{ mt: 2 }} />
          <CustomerBankInfo customer={findCustomer(customerIdRepay)} labelPrefix="ฝั่งคืน — " sx={{ mt: 1 }} />

          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button onClick={cancelEdit} disabled={saving}>ยกเลิก</Button>
            <Button variant="contained" startIcon={<SaveIcon />} onClick={submit} disabled={!canSave || saving}>
              {saving ? 'กำลังบันทึก...' : 'บันทึก'}
            </Button>
          </Box>
        </Box>
      ) : (
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ md: 'center' }}>
          <Stack direction="row" spacing={{ xs: 1, sm: 2 }} alignItems="stretch" sx={{ minWidth: 0, flex: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0, p: 1.5, borderRadius: 2.5, bgcolor: '#eff6ff', border: '1px solid #bfdbfe' }}>
              <Typography variant="caption" sx={{ color: '#2563eb', fontWeight: 700 }}>ฝั่งยืม</Typography>
              <Typography sx={{ fontWeight: 750, color: '#0f172a', overflowWrap: 'anywhere' }}>{customerName(pair.customer_id)}</Typography>
            </Box>
            <Box sx={{ alignSelf: 'center', width: 34, height: 34, borderRadius: '50%', display: 'grid', placeItems: 'center', bgcolor: '#f1f5f9', color: '#475569', flexShrink: 0 }}><ArrowIcon fontSize="small" /></Box>
            <Box sx={{ flex: 1, minWidth: 0, p: 1.5, borderRadius: 2.5, bgcolor: '#ecfdf5', border: '1px solid #a7f3d0' }}>
              <Typography variant="caption" sx={{ color: '#059669', fontWeight: 700 }}>ฝั่งคืน</Typography>
              <Typography sx={{ fontWeight: 750, color: '#0f172a', overflowWrap: 'anywhere' }}>{customerName(pair.customer_id_repay)}</Typography>
            </Box>
          </Stack>
          <Button variant="outlined" startIcon={<EditIcon />} onClick={startEdit} sx={{ alignSelf: { xs: 'flex-end', md: 'center' } }}>แก้ไขคู่บัญชี</Button>
        </Stack>
      )}
    </Paper>
  );
}

export default LoanPairSetupCard;
